import React, { useRef, useState, useMemo, useEffect, useCallback } from 'react';
import { Root, Arrow, MainElementContainer, ContextContainer, Label, ListWrapper, ChildWrapper, ChildContainer, } from './styles';
import { Button, BUTTON_TYPES } from '../Button/Button';
import { THEME_LAYOUT_TYPES } from '../../types';
import { cx } from '@linaria/core';
import { useFloating, useClick, useInteractions, useDismiss, FloatingArrow, arrow, offset, useTransitionStatus, flip, shift, FloatingPortal, FloatingFocusManager, autoUpdate, } from '@floating-ui/react';
import { doNothing } from '../misc';
import KeyCode from '../shared/keyCode';
import { Typo } from '../primitives/Typo';
import { useTheme } from '../ThemeProvider/ThemeProvider';
export var AnimationType;
(function (AnimationType) {
    AnimationType["DEFAULT"] = "default";
    AnimationType["RIGHT_TO_LEFT"] = "rightToLeft";
    AnimationType["TOP_TO_BOTTOM"] = "topToBottom";
})(AnimationType || (AnimationType = {}));
function useButtons(items, setIsOpen) {
    return useMemo(() => items &&
        items.map((item, i) => {
            if (item.label) {
                return (React.createElement("span", { key: i, className: cx(Label.base, Typo.base, Typo.label03) }, item.label));
            }
            return (React.createElement("li", { className: cx(item.hasBottomBorder && ContextContainer.hasBottomBorder, Typo.base, Typo.label01), role: "presentation", key: i, onClick: () => {
                    setIsOpen(false);
                } },
                React.createElement(Button, { type: BUTTON_TYPES.SQUARE, hasMinimalActiveStyle: true, tabIndex: -1, ...item, accessibility: item.accessibility ?? 'menuitem' })));
        }), [items]);
}
function getAnimationClass(hasDisabledAnimations, animationType) {
    if (!hasDisabledAnimations) {
        if (animationType === AnimationType.DEFAULT) {
            return ContextContainer.hasDefaultAnimation;
        }
        if (animationType === AnimationType.TOP_TO_BOTTOM) {
            return ContextContainer.hasDropdownAnimation;
        }
        if (animationType === AnimationType.RIGHT_TO_LEFT) {
            return ContextContainer.hasRightToLeftAnimation;
        }
    }
}
export function ContextMenu({ children, items, hasDisabledAnimations, isOpen = false, onOpen = doNothing, onClose = doNothing, appendTo, isFullwidth, hasArrow = true, contextMenuRef, id = 'context-menu', animationType = AnimationType.DEFAULT, }) {
    /** Variables */
    const { layout } = useTheme();
    const [open, setOpen] = useState(isOpen);
    const [isScrollEnd, setIsScrollEnd] = useState(false);
    const [focusIndex, setFocusIndex] = useState(-1);
    const scrollRef = useRef(null);
    const arrowRef = useRef(null);
    const contextMenuWrapperRef = useRef(null);
    const { refs, floatingStyles, context, placement } = useFloating({
        middleware: [
            flip(),
            shift({
                padding: isFullwidth ? 0 : 10,
            }),
            arrow({
                element: arrowRef,
            }),
            offset(hasArrow ? 10 : 2),
        ],
        open: open,
        onOpenChange: setOpen,
        whileElementsMounted: autoUpdate,
    });
    const click = useClick(context);
    const dismiss = useDismiss(context);
    const { isMounted, status } = useTransitionStatus(context, { duration: hasDisabledAnimations ? 0 : 200 });
    const { getReferenceProps } = useInteractions([click, dismiss]);
    const onKeyDown = useCallback((e) => {
        if (!items) {
            return;
        }
        if (e.keyCode === KeyCode.ARROW_UP) {
            e.preventDefault();
            setFocusIndex(focusIndex > 0 ? focusIndex - 1 : items.length - 1);
            return;
        }
        if (e.keyCode === KeyCode.ARROW_DOWN) {
            e.preventDefault();
            setFocusIndex(focusIndex < items.length - 1 ? focusIndex + 1 : 0);
            return;
        }
        if (e.keyCode === KeyCode.ENTER) {
            e.preventDefault();
            items[focusIndex].onClick?.(e);
            setOpen(false);
            return;
        }
    }, [focusIndex, items]);
    const buttonsMemo = useButtons(items, setOpen);
    const handleScroll = () => {
        const container = scrollRef?.current;
        if (container && container.scrollTop + container.clientHeight >= container.scrollHeight) {
            setIsScrollEnd(true);
        }
        else if (isScrollEnd) {
            setIsScrollEnd(false);
        }
    };
    useEffect(() => {
        if (open) {
            onOpen();
            const container = scrollRef?.current;
            if (container && container?.scrollHeight > container?.clientHeight) {
                setIsScrollEnd(false);
            }
            else {
                setIsScrollEnd(true);
            }
        }
        else {
            onClose();
        }
    }, [open, scrollRef.current]);
    useEffect(() => {
        if (scrollRef.current) {
            setTimeout(() => scrollRef.current?.querySelectorAll('li > button')[focusIndex]?.focus(), 0);
        }
    }, [focusIndex, scrollRef.current]);
    const appendToComputed = appendTo ?? contextMenuWrapperRef.current;
    const Portal = appendToComputed ? FloatingPortal : 'div';
    /** JSX */
    return (React.createElement("div", { ref: contextMenuWrapperRef, className: cx(Root.base) },
        React.createElement("div", { ref: refs.setReference, ...getReferenceProps(), className: cx(MainElementContainer.base) }, children),
        isMounted && (React.createElement(Portal, { root: appendToComputed },
            React.createElement(FloatingFocusManager, { order: ['floating', 'content'], context: context, guards: true, modal: true, visuallyHiddenDismiss: "Men\u00FC schlie\u00DFen" },
                React.createElement("div", { id: id, ref: refs.setFloating, style: floatingStyles, className: cx(ContextContainer.base, isFullwidth && ContextContainer.isFullwidth, !isScrollEnd && ContextContainer.hasPaddingBottom, layout === THEME_LAYOUT_TYPES.CHILD && ChildContainer.base, getAnimationClass(!!hasDisabledAnimations, animationType)), "data-status": status, "data-placement": placement, onKeyDown: onKeyDown },
                    React.createElement("div", { ref: contextMenuRef, className: cx(ListWrapper.base, !hasDisabledAnimations && ListWrapper.hasAnimations, layout === THEME_LAYOUT_TYPES.CHILD && ChildWrapper.base) },
                        hasArrow && (React.createElement(FloatingArrow, { ref: arrowRef, context: context, className: cx(Arrow.base) })),
                        React.createElement("ul", { ref: scrollRef, role: "menu", onScroll: handleScroll }, buttonsMemo))))))));
}
export default React.memo(ContextMenu);
